import React, { useState, useEffect, useCallback } from 'react';
import { api, apiFetch } from '../lib/api';

// Admin Broadcasts page — compose announcements that fan out as a
// push notification to every viewer of the selected artist channels.
// Supports multi-channel sends (artistIds[]), an optional hero image,
// and a list of past broadcasts with recipient counts. Mirrors the
// iOS takeover-card content so admins can preview the basics here.

function fmtRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff/86400)}d ago`;
  return d.toLocaleDateString('en-US', { month:'short', day:'numeric' });
}

export default function BroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, artistList] = await Promise.all([
        api.listBroadcasts(),
        apiFetch('/artists'),
      ]);
      setBroadcasts(list || []);
      setArtists(Array.isArray(artistList) ? artistList : (artistList?.artists || []));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this broadcast? Viewers will lose access to it.')) return;
    try {
      await api.deleteBroadcast(id);
      setBroadcasts(prev => prev.filter(b => b.id !== id));
    } catch (e) { alert(e.message); }
  };

  return (
    <div style={{ padding:'24px 24px 80px', maxWidth:760, margin:'0 auto' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h1 style={{ fontSize:28, fontWeight:900, margin:0, color:'var(--text)', letterSpacing:-0.5 }}>Broadcasts</h1>
          <p style={{ fontSize:13, color:'var(--text3)', margin:'4px 0 0' }}>Push announcements to viewers across one or more artist channels.</p>
        </div>
        <button onClick={() => setComposeOpen(true)}
          style={{ padding:'10px 18px', borderRadius:12, background:'linear-gradient(135deg,#1E40AF,#06B6D4)', border:'none', color:'#fff', fontSize:13, fontWeight:800, cursor:'pointer', fontFamily:'inherit', display:'inline-flex', alignItems:'center', gap:8 }}>
          <i className="fa-solid fa-bullhorn" style={{ fontSize:12 }}></i> New Broadcast
        </button>
      </div>

      {loading ? (
        <p style={{ color:'var(--text3)' }}>Loading…</p>
      ) : broadcasts.length === 0 ? (
        <div style={{ padding:'48px 24px', textAlign:'center', border:'1px dashed var(--border)', borderRadius:16, background:'var(--card)' }}>
          <i className="fa-solid fa-bullhorn" style={{ fontSize:32, color:'var(--text3)', display:'block', marginBottom:12 }}></i>
          <p style={{ fontSize:15, fontWeight:700, color:'var(--text)', margin:'0 0 4px' }}>No broadcasts yet</p>
          <p style={{ fontSize:13, color:'var(--text3)', margin:0 }}>Use this for tour-wide announcements, schedule changes, or anything every viewer needs to see.</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {broadcasts.map(b => (
            <div key={b.id} style={{ padding:16, borderRadius:14, background:'var(--card)', border:'1px solid var(--border)' }}>
              <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
                {b.imageUrl && (
                  <div style={{ width:64, height:64, borderRadius:10, overflow:'hidden', flexShrink:0, background:'var(--bg3)' }}>
                    <img src={b.imageUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  </div>
                )}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:10 }}>
                    <p style={{ fontSize:15, fontWeight:800, color:'var(--text)', margin:0, lineHeight:1.3 }}>{b.headline}</p>
                    <span style={{ fontSize:11, color:'var(--text3)', flexShrink:0, fontWeight:600 }}>{fmtRelative(b.createdAt)}</span>
                  </div>
                  {b.message && (
                    <p style={{ fontSize:13, color:'var(--text2)', margin:'6px 0 0', lineHeight:1.5, whiteSpace:'pre-wrap' }}>{b.message}</p>
                  )}
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, flexWrap:'wrap' }}>
                    {(b.artistIds || []).map(id => {
                      const a = artists.find(x => x.id === id);
                      return (
                        <span key={id} style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'3px 10px', borderRadius:20, background:`${a?.color || '#94a3b8'}18`, border:`1px solid ${a?.color || '#94a3b8'}40`, color:a?.color || 'var(--text2)', fontSize:11, fontWeight:700 }}>
                          <span style={{ width:6, height:6, borderRadius:'50%', background:a?.color || '#94a3b8' }} />
                          {a?.name || 'Unknown'}
                        </span>
                      );
                    })}
                    {b.recipientCount != null && (
                      <span style={{ fontSize:11, color:'var(--text3)', fontWeight:600 }}>· Sent to {b.recipientCount} viewer{b.recipientCount === 1 ? '' : 's'}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => handleDelete(b.id)}
                  title="Delete broadcast"
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:14, padding:6, fontFamily:'inherit', flexShrink:0 }}>
                  <i className="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {composeOpen && (
        <ComposeModal artists={artists} onClose={() => setComposeOpen(false)} onSent={() => { setComposeOpen(false); load(); }} />
      )}
    </div>
  );
}

function ComposeModal({ artists, onClose, onSent }) {
  const [headline, setHeadline] = useState('');
  const [message, setMessage] = useState('');
  const [selectedArtistIds, setSelectedArtistIds] = useState(artists.length === 1 ? [artists[0].id] : []);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageName, setImageName] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const toggleArtist = (id) => {
    setSelectedArtistIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImage = async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise(r => { reader.onload = () => r(reader.result); reader.readAsDataURL(file); });
      const res = await api.uploadFile({ name: file.name, type: file.type, data: dataUrl });
      // uploadFile returns { name, type, url } — url is a relative path
      // like /uploads/<file>. Resolve against the API host so the iOS
      // viewer renders it on a different origin.
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');
      const absolute = res.url.startsWith('http') ? res.url : `${apiBase}${res.url}`;
      setImageUrl(absolute);
      setImageName(file.name);
    } catch (e) {
      setError('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSend = async () => {
    if (!headline.trim()) { setError('Headline is required'); return; }
    if (!selectedArtistIds.length) { setError('Pick at least one channel'); return; }
    setSending(true);
    setError('');
    try {
      await api.createBroadcast({
        artistIds: selectedArtistIds,
        headline: headline.trim(),
        message: message.trim() || null,
        imageUrl: imageUrl || null,
      });
      onSent();
    } catch (e) {
      setError(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'var(--bg2)', borderRadius:18, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto', border:'1px solid var(--border)' }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <h2 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>New Broadcast</h2>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:18, padding:0 }}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:18 }}>
          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:800, color:'var(--text3)', letterSpacing:1, marginBottom:8, textTransform:'uppercase' }}>Channels</label>
            <p style={{ fontSize:12, color:'var(--text3)', margin:'0 0 10px' }}>Each artist's viewers get the push. Pick more than one to broadcast to multiple tours at once.</p>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              {artists.map(a => {
                const sel = selectedArtistIds.includes(a.id);
                return (
                  <button key={a.id} type="button" onClick={() => toggleArtist(a.id)}
                    style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'8px 14px', borderRadius:24, cursor:'pointer', fontFamily:'inherit', fontSize:13, fontWeight:700,
                      background: sel ? `${a.color || '#1E40AF'}22` : 'var(--bg3)',
                      border: `1.5px solid ${sel ? (a.color || '#1E40AF') : 'var(--border)'}`,
                      color: sel ? (a.color || 'var(--blue)') : 'var(--text2)' }}>
                    <span style={{ width:8, height:8, borderRadius:'50%', background:a.color || '#94a3b8' }} />
                    {a.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:800, color:'var(--text3)', letterSpacing:1, marginBottom:6, textTransform:'uppercase' }}>Headline</label>
            <input value={headline} onChange={e => setHeadline(e.target.value)} maxLength={140} autoFocus
              placeholder="What's the announcement?"
              style={{ width:'100%', background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', fontSize:15, color:'var(--text)', outline:'none', fontFamily:'inherit', boxSizing:'border-box' }} />
            <p style={{ fontSize:11, color:'var(--text3)', margin:'6px 0 0', textAlign:'right' }}>{headline.length} / 140</p>
          </div>

          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:800, color:'var(--text3)', letterSpacing:1, marginBottom:6, textTransform:'uppercase' }}>Message <span style={{ color:'var(--text3)', fontWeight:500, textTransform:'none', letterSpacing:0 }}>(optional)</span></label>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
              placeholder="Add details. Viewers can expand the takeover card to read the full message."
              style={{ width:'100%', background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 14px', fontSize:14, color:'var(--text)', outline:'none', resize:'vertical', fontFamily:'inherit', boxSizing:'border-box', lineHeight:1.5 }} />
          </div>

          <div>
            <label style={{ display:'block', fontSize:11, fontWeight:800, color:'var(--text3)', letterSpacing:1, marginBottom:6, textTransform:'uppercase' }}>Image <span style={{ color:'var(--text3)', fontWeight:500, textTransform:'none', letterSpacing:0 }}>(optional)</span></label>
            {imageUrl ? (
              <div style={{ display:'flex', alignItems:'center', gap:12, padding:10, borderRadius:10, background:'var(--card)', border:'1px solid var(--border)' }}>
                <img src={imageUrl} alt="" style={{ width:64, height:64, borderRadius:8, objectFit:'cover' }} />
                <div style={{ flex:1, fontSize:13, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{imageName || 'Image'}</div>
                <button onClick={() => { setImageUrl(null); setImageName(null); }}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--red)', fontSize:14, padding:6, fontFamily:'inherit' }}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>
            ) : (
              <label style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, padding:'18px', borderRadius:10, background:'var(--bg3)', border:'2px dashed var(--border)', cursor:'pointer', fontSize:13, color:'var(--text3)' }}>
                <i className="fa-solid fa-image" style={{ fontSize:18 }}></i>
                {uploading ? 'Uploading…' : 'Click to attach an image'}
                <input type="file" accept="image/*" style={{ display:'none' }}
                  onChange={e => handleImage(e.target.files?.[0])} />
              </label>
            )}
          </div>

          {error && (
            <div style={{ padding:'10px 14px', borderRadius:10, background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:13, fontWeight:600 }}>{error}</div>
          )}
        </div>

        <div style={{ padding:'16px 24px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose}
            style={{ padding:'11px 20px', borderRadius:10, background:'var(--bg3)', border:'1px solid var(--border)', color:'var(--text2)', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
            Cancel
          </button>
          <button onClick={handleSend} disabled={sending || !headline.trim() || !selectedArtistIds.length}
            style={{ padding:'11px 22px', borderRadius:10, background:'linear-gradient(135deg,#1E40AF,#06B6D4)', border:'none', color:'#fff', fontSize:13, fontWeight:800, cursor: sending ? 'default' : 'pointer', fontFamily:'inherit', opacity: (!headline.trim() || !selectedArtistIds.length) ? 0.5 : 1 }}>
            {sending ? 'Sending…' : `Send to ${selectedArtistIds.length || 0} channel${selectedArtistIds.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </div>
  );
}
